import Breadcrumbs from "@/components/Breadcrumbs";
import { SettingsService } from "@/services/admin-services";
import { SettingsFootnote } from "@/services/admin-services/models";
import { LocaleService } from "@/services/global-services";
import { Language, Locale } from "@/services/global-services/models";
import { ArchiveBoxArrowDownIcon, XMarkIcon } from "@heroicons/react/24/outline";
import {
    Alert,
    AlertColor,
    Box,
    Button,
    CircularProgress,
    Collapse,
    Divider,
    Grid,
    IconButton,
    TextField,
    Typography,
} from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

const breadcrumbs = [
    {
        to: "../../..",
        title: "Administration",
    },
    {
        title: "Settings",
        to: "../../..",
    },
    {
        title: "Footnotes",
        to: "..",
    },
];

const Details = () => {
    const [breadcrumbTrail, setBreadcrumbTrail] = useState(breadcrumbs);
    const [data, setData] = useState<SettingsFootnote>();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isSaving, setIsSaving] = useState<boolean>();
    const [translations, setTranslations] = useState<Record<string, string>>({});
    const [languages, setLanguages] = useState<Array<Language>>([]);

    const [notification, setNotification] = useState<{
        open: boolean;
        message?: string;
        type: AlertColor;
    }>({ open: false, type: "success" });

    const notificationTimerRef = useRef<NodeJS.Timeout | null>(null);

    const settingsService = useMemo(() => new SettingsService("int"), []);

    const navigate = useNavigate();
    const { identifier } = useParams();

    useEffect(() => {
        Promise.all([
            settingsService.getFootnote(identifier!).then((data) => setData(data)),
            LocaleService.get().then(parseLanguages),
        ]).then(() => setIsLoading(false));

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!data) return;
        setBreadcrumbTrail([...breadcrumbs, { title: `${data.key}`, to: "" }]);
    }, [data]);

    useEffect(() => {
        if (!data || !languages || (translations && Object.keys(translations).length > 0)) return;

        const translationPerLanguage: Record<string, string> = {};
        for (let i = 0; i < languages.length; i++) {
            const language = languages[i];
            const translation = data.labels.find((x) => x.languageCode === language.slug);

            translationPerLanguage[language.slug] = translation?.label || "";
        }

        setTranslations(translationPerLanguage);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, languages]);

    useEffect(() => {
        if (!notification.open) return;

        notificationTimerRef.current = setTimeout(() => hideNotification(), 3000);
    }, [notification]);

    const parseLanguages = (regions: Array<Locale>) => {
        const languages: Array<Language> = [];
        for (let i = 0; i < regions.length; i++) {
            const region = regions[i];

            for (let j = 0; j < region.languages.length; j++) {
                const language = region.languages[j];
                if (languages.findIndex((x) => x.label === language.label) > -1) continue;

                const split = language.slug.split("-");
                languages.push({
                    ...language,
                    slug: `int-${split[1]}`,
                    regionLabel: "International",
                });
            }
        }

        setLanguages(languages.sort((a, b) => a.label.localeCompare(b.label)));
    };

    const displayNotification = (message: string, type: AlertColor) => setNotification({ open: true, message, type });
    const hideNotification = () => setNotification({ open: false, type: "success" });

    const onCancel = () => navigate("..");

    const onSave = () => {
        if (!data) return;

        setIsSaving(true);

        settingsService
            .updateFootnote(data.key, {
                ...data,
                labels: Object.keys(translations).map((key) => ({ languageCode: key, label: translations[key] })),
            })
            .then((result) => {
                setData({ ...result, key: data.key });
                displayNotification("Translations updated successfully.", "success");
                setIsSaving(false);
            });
    };

    if (isLoading || !data)
        return (
            <Box>
                <Breadcrumbs trail={breadcrumbs} />

                <Divider />
                <Box m={4} sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 500 }}>
                    <CircularProgress />
                </Box>
            </Box>
        );

    return (
        <Box>
            <Breadcrumbs trail={breadcrumbTrail} />

            <Divider />

            <Box m={4} sx={{ display: "flex" }}>
                <Box sx={{ display: "flex", flex: 1, flexDirection: "column" }}>
                    <Typography variant={"h4"}>{data.key}</Typography>
                </Box>
            </Box>

            <Divider />

            <Grid container spacing={2} p={{ xs: 2, md: 4 }}>
                <Box sx={{ width: "100%" }}>
                    <Collapse in={notification.open}>
                        <Alert
                            action={
                                <IconButton
                                    aria-label="close"
                                    color="inherit"
                                    size="small"
                                    onClick={() => hideNotification()}
                                >
                                    <XMarkIcon className="h-5 w-5" />
                                </IconButton>
                            }
                            severity={notification.type}
                        >
                            {notification.message}
                        </Alert>
                    </Collapse>
                </Box>

                <Grid size={{ xs: 12, md: 4 }}>
                    <Typography variant="h6" gutterBottom fontWeight={700}>
                        Labels
                    </Typography>
                    <Typography variant="subtitle2" color={"text.secondary"}>
                        Configure translations.
                    </Typography>
                </Grid>

                <Grid size={{ xs: 12, md: 8 }}>
                    <Grid container spacing={4}>
                        {Object.keys(translations).length > 0 &&
                            languages
                                .sort((a, b) => a.label.localeCompare(b.label))
                                .map((language) => (
                                    <Grid size={{ xs: 12, sm: 12 }} key={language.slug}>
                                        <Typography variant={"subtitle2"} sx={{ mb: 2 }} fontWeight={700}>
                                            {language.label}
                                        </Typography>
                                        <TextField
                                            label="Translation"
                                            variant="outlined"
                                            fullWidth
                                            value={translations[language.slug]}
                                            multiline
                                            rows={3}
                                            onChange={(e) =>
                                                setTranslations({
                                                    ...translations,
                                                    [language.slug]: e.currentTarget.value,
                                                })
                                            }
                                        />
                                    </Grid>
                                ))}

                        <Grid size={{ xs: 12 }} sx={{ display: "flex", alignContent: "flex-end" }}>
                            <Button
                                variant="contained"
                                color="error"
                                sx={{ ml: "auto" }}
                                onClick={() => onCancel()}
                                disabled={isSaving}
                            >
                                Cancel
                            </Button>
                            <Box sx={{ ml: 1, position: "relative" }}>
                                <Button
                                    variant="contained"
                                    startIcon={<ArchiveBoxArrowDownIcon className="h-5 w-5" />}
                                    sx={{ ml: 1 }}
                                    onClick={() => onSave()}
                                >
                                    Save
                                </Button>
                                {isSaving && (
                                    <CircularProgress
                                        size={24}
                                        sx={{
                                            position: "absolute",
                                            top: "50%",
                                            left: "50%",
                                            marginTop: "-12px",
                                            marginLeft: "-12px",
                                        }}
                                    />
                                )}
                            </Box>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </Box>
    );
};

export default Details;
